import React, { useState, useMemo } from 'react';
import { Button, Checkbox, DropZone, Icon, Spinner, Text } from '@shopify/polaris';
import { NoteIcon, PlusIcon } from '@shopify/polaris-icons';
import Language from '../../../../../i18n/Language';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    arrayMove,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const t = Language;

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
// 收起状态下最多展示的图片数量（超出用 +N 折叠）
const COLLAPSED_COUNT = 7;

// 单个可拖拽图片格子
function SortableMediaItem({
    id,
    image,
    index,
    isMain,
    selected,
    disabled,
    onToggleSelect,
    selectedIndexes,
}) {
    // 选中多于 1 张时禁止拖拽（仅关拖拽，不影响勾选）
    const dragDisabled = selectedIndexes.length > 0;
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: disabled || dragDisabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 'auto',
        opacity: isDragging ? 0.85 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`pm_item ${isMain ? 'pm_item_main' : ''} ${selected ? 'pm_item_selected' : ''} ${isDragging ? 'pm_item_dragging' : ''} ${dragDisabled ? 'pm_item_no_drag' : ''}`}
            {...attributes}
            {...listeners}
        >
            <img
                className="pm_item_img"
                src={image.src || image.data || NoteIcon}
                alt={image.alt || image.altText || ''}
                draggable={false}
            />

            {/* 选中态遮罩 + 勾选框。
                注意：不要在这里 stopPropagation onPointerDown，否则会拦截 dnd-kit 挂在
                父节点上的拖拽监听导致拖不动。PointerSensor 的 distance:5 已能区分点击与拖拽。 */}
            <div
                className="pm_item_mask"
                style={selectedIndexes.length > 0 ? { opacity: 1, zIndex: 1, backgroundColor: selected ? '#ffffff66' : 'transparent' } : {}}
                onClick={(e) => {
                    if (disabled) return;
                    e.stopPropagation();
                    onToggleSelect(index);
                }}
            >
                <div className="pm_item_check">
                    <Checkbox
                        label=""
                        labelHidden
                        checked={selected}
                        disabled={disabled}
                        onChange={() => { }}
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * 产品多图网格：主图大格 + 换行小图 + 收起/展开 + 添加块 + 拖拽排序
 * 仅做样式与交互，具体后端数据由父组件通过回调处理
 */
export default function ProductMediaGrid({
    images = [],
    loading = false,
    disabled = false,
    onUpload,
    onRemove,
    onEditAlt,
    onReorder,
}) {
    const [expanded, setExpanded] = useState(false);
    const [selectedIndexes, setSelectedIndexes] = useState([]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // 给每张图一个稳定 id 用于拖拽
    const itemIds = useMemo(
        () => images.map((img, i) => String(img.id ?? img.src ?? img.data ?? i) + '_' + i),
        [images],
    );

    const visibleCount = expanded ? images.length : Math.min(images.length, COLLAPSED_COUNT);
    const hiddenCount = images.length - visibleCount;

    const handleToggleSelect = (index) => {
        setSelectedIndexes((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
        );
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = itemIds.indexOf(active.id);
        const newIndex = itemIds.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const next = arrayMove(images, oldIndex, newIndex);
        onReorder && onReorder(next);
        setSelectedIndexes([]);
    };

    const handleDropZoneDrop = (files) => {
        if (!files || files.length === 0) return;
        onUpload && onUpload(files);
    };

    const handleEditAltSelected = () => {
        onEditAlt && onEditAlt(selectedIndexes);
    };

    const handleRemoveSelected = () => {
        onRemove && onRemove(selectedIndexes);
        setSelectedIndexes([]);
    };

    const hasSelection = selectedIndexes.length > 0;
    const allSelected = images.length > 0 && selectedIndexes.length === images.length;
    const checkboxState = allSelected ? true : (hasSelection ? 'indeterminate' : false);

    const handleToggleSelectAll = () => {
        setSelectedIndexes(allSelected ? [] : images.map((_, i) => i));
    };

    // const handleSelectAll = () => {
    //     setSelectedIndexes(images.map((_, i) => i));
    // };

    return (
        <div className={`product_media_container ${disabled ? 'disabled_upload' : ''}`}>
            <div className="pm_header">
                {hasSelection ? (
                    <div className="pm_header_select">
                        <Checkbox
                            // label={`${selectedIndexes.length} ${selectedIndexes.length <= 1 ? t('file selected') : t('files selected')}`}
                            label={`${selectedIndexes.length} ${t('selected')}`}
                            checked={checkboxState}
                            disabled={disabled}
                            onChange={handleToggleSelectAll}
                        />
                        {/* {!allSelected ? (
                            <Button
                                variant="plain"
                                disabled={disabled}
                                onClick={handleSelectAll}
                            >
                                {t('Select all')}
                            </Button>
                        ) : null} */}
                    </div>
                ) : (
                    <div className='flex_layout'>
                        <Text variant="bodyMd" as="p">{t('Media')}</Text>

                        {expanded && images.length > COLLAPSED_COUNT ? (
                            <Button variant="plain" onClick={() => setExpanded(false)}>
                                {t('Show less')}
                            </Button>
                        ) : null}
                    </div>
                )}
                {hasSelection ? (
                    <div className="pm_header_actions">
                        <Button
                            variant="plain"
                            disabled={disabled}
                            onClick={handleEditAltSelected}
                        >
                            {t('Edit alt text')}
                        </Button>
                        <Button
                            variant="plain"
                            tone="critical"
                            disabled={disabled}
                            onClick={handleRemoveSelected}
                        >
                            {t('Remove')}
                        </Button>
                    </div>
                ) : null}
            </div>

            {images.length === 0 ? (
                loading ? (
                    <div className="pm_empty_loading">
                        <Spinner size="small" />
                    </div>
                ) : (
                    <div className="pm_empty">
                        <DropZone
                            accept="image/png,image/jpeg,image/jpg"
                            type="image"
                            errorOverlayText={t("File type must be .jpg .jpeg .png.")}
                            disabled={disabled}
                            onDrop={handleDropZoneDrop}
                            allowMultiple
                            variableHeight
                        >
                            <DropZone.FileUpload actionHint={t("Accepts .jpg, and .png")} />
                        </DropZone>
                    </div>
                )
            ) : (
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={itemIds} strategy={rectSortingStrategy}>
                    <div className="pm_grid">
                        {images.slice(0, visibleCount).map((image, index) => {
                            const isLastVisible = !expanded && hiddenCount > 0 && index === visibleCount - 1;
                            return (
                                <div className="pm_cell" key={itemIds[index]}>
                                    <SortableMediaItem
                                        id={itemIds[index]}
                                        image={image}
                                        index={index}
                                        isMain={index === 0}
                                        selected={selectedIndexes.includes(index)}
                                        disabled={disabled}
                                        onToggleSelect={handleToggleSelect}
                                        selectedIndexes={selectedIndexes}
                                    />
                                    {isLastVisible ? (
                                        <div
                                            className="pm_more"
                                            onClick={() => setExpanded(true)}
                                        >
                                            {`+${hiddenCount}`}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}

                        {/* 上传中：在添加按钮前追加 loading 格子 */}
                        {loading ? (
                            <div className="pm_cell pm_loading_cell">
                                <Spinner size="small" />
                            </div>
                        ) : null}

                        {/* 添加块 */}
                        <div className="pm_add">
                            <DropZone
                                accept="image/png,image/jpeg,image/jpg"
                                type="image"
                                disabled={disabled || loading}
                                onDrop={handleDropZoneDrop}
                                allowMultiple
                                variableHeight
                            >
                                <div className="pm_add_inner">
                                    <span className="pm_add_icon">
                                        <Icon source={PlusIcon} tone="base" />
                                    </span>
                                </div>
                            </DropZone>
                        </div>
                    </div>
                </SortableContext>
            </DndContext>
            )}
        </div>
    );
}

export { VALID_IMAGE_TYPES };
